import { BaseGrid } from './BaseGrid';
import { BaseGridRenderer } from './BaseGridRenderer';
import { Cell, CellType } from './Cell';
import { ViewportController } from '../visualization/ViewportController';

export enum DrawMode {
  PAINT = 'paint',
  START = 'start',
  GOAL = 'goal'
}

type PreviewType = 'wall' | 'erase' | 'start' | 'goal' | null;

function drawModeToPreviewType(mode: DrawMode, isErasing: boolean = false): PreviewType {
  switch (mode) {
    case DrawMode.PAINT:
      return isErasing ? 'erase' : 'wall';
    case DrawMode.START:
      return 'start';
    case DrawMode.GOAL:
      return 'goal';
    default:
      return null;
  }
}

export class GridInteraction {
  private grid: BaseGrid;
  private renderer: BaseGridRenderer;
  private canvas: HTMLCanvasElement;
  private viewportController?: ViewportController;
  private isDrawing: boolean = false;
  private isErasing: boolean = false;
  private isPainting: boolean = false;
  private lastPaintedCell: Cell | null = null;
  private currentDrawMode: DrawMode = DrawMode.PAINT;
  private startPlaced: boolean = false;
  private goalPlaced: boolean = false;
  private hoverCell: Cell | null = null;
  private onPostRender?: () => void;

  private boundMouseDown: (event: MouseEvent) => void;
  private boundMouseMove: (event: MouseEvent) => void;
  private boundMouseUp: () => void;
  private boundMouseLeave: () => void;
  private boundContextMenu: (event: MouseEvent) => void;
  private boundTouchStart: (event: TouchEvent) => void;
  private boundTouchMove: (event: TouchEvent) => void;
  private boundTouchEnd: () => void;

  constructor(grid: BaseGrid, renderer: BaseGridRenderer, viewportController?: ViewportController) {
    this.grid = grid;
    this.renderer = renderer;
    this.canvas = renderer.getCanvas();
    this.viewportController = viewportController;

    this.boundMouseDown = this.handleMouseDown.bind(this);
    this.boundMouseMove = this.handleMouseMove.bind(this);
    this.boundMouseUp = this.handleMouseUp.bind(this);
    this.boundMouseLeave = this.handleMouseLeave.bind(this);
    this.boundContextMenu = this.handleContextMenu.bind(this);
    this.boundTouchStart = this.handleTouchStart.bind(this);
    this.boundTouchMove = this.handleTouchMove.bind(this);
    this.boundTouchEnd = this.handleTouchEnd.bind(this);

    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    this.canvas.addEventListener('mousedown', this.boundMouseDown);
    this.canvas.addEventListener('mousemove', this.boundMouseMove);
    this.canvas.addEventListener('mouseup', this.boundMouseUp);
    this.canvas.addEventListener('mouseleave', this.boundMouseLeave);
    this.canvas.addEventListener('contextmenu', this.boundContextMenu);
    this.canvas.addEventListener('touchstart', this.boundTouchStart, { passive: false });
    this.canvas.addEventListener('touchmove', this.boundTouchMove, { passive: false });
    this.canvas.addEventListener('touchend', this.boundTouchEnd);
  }

  private handleMouseDown(event: MouseEvent): void {
    if (event.button === 1) return;

    event.preventDefault();

    if (this.currentDrawMode === DrawMode.PAINT) {
      this.isDrawing = true;
      this.isErasing = event.button === 2;
      this.lastPaintedCell = null;

      const coords = this.getCellFromMousePosition(event);
      if (coords) {
        const cell = this.grid.getCell(coords.row, coords.col);
        if (cell && cell.type !== CellType.START && cell.type !== CellType.GOAL) {
          if (this.isErasing) {
            this.isPainting = false;
          } else {
            this.isPainting = cell.type !== CellType.WALL;
          }
        }
      }

      this.handlePainting(event);
    } else if (event.button === 0) {
      this.handleDrawing(event);
    }
  }

  private handleMouseMove(event: MouseEvent): void {
    const coords = this.getCellFromMousePosition(event);
    if (coords) {
      const cell = this.grid.getCell(coords.row, coords.col);
      if (cell && cell !== this.hoverCell) {
        this.hoverCell = cell;
        this.renderer.render(this.hoverCell, drawModeToPreviewType(this.currentDrawMode, this.isErasing));
        this.onPostRender?.();
      }
    }

    if (this.isDrawing && this.currentDrawMode === DrawMode.PAINT) {
      this.handlePainting(event);
    } else if (this.isDrawing) {
      this.handleDrawing(event);
    }
  }

  private handleMouseUp(): void {
    this.isDrawing = false;
    this.lastPaintedCell = null;
  }

  private handleMouseLeave(): void {
    this.isDrawing = false;
    this.lastPaintedCell = null;
    this.hoverCell = null;
    this.renderer.render();
    this.onPostRender?.();
  }

  private handleContextMenu(event: MouseEvent): void {
    event.preventDefault();
  }

  private handleTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    if (this.currentDrawMode === DrawMode.PAINT) {
      this.isDrawing = true;
      this.isErasing = false;
      this.lastPaintedCell = null;

      const coords = this.getCellFromTouchPosition(touch);
      if (coords) {
        const cell = this.grid.getCell(coords.row, coords.col);
        if (cell && cell.type !== CellType.START && cell.type !== CellType.GOAL) {
          this.isPainting = cell.type !== CellType.WALL;
        }
      }

      this.handleTouchPainting(touch);
    } else {
      this.handleTouchDrawing(touch);
    }
  }

  private handleTouchMove(event: TouchEvent): void {
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    const coords = this.getCellFromTouchPosition(touch);
    if (coords) {
      const cell = this.grid.getCell(coords.row, coords.col);
      if (cell && cell !== this.hoverCell) {
        this.hoverCell = cell;
        this.renderer.render(this.hoverCell, drawModeToPreviewType(this.currentDrawMode, this.isErasing));
        this.onPostRender?.();
      }
    }

    if (this.isDrawing && this.currentDrawMode === DrawMode.PAINT) {
      this.handleTouchPainting(touch);
    }
  }

  private handleTouchEnd(): void {
    this.isDrawing = false;
    this.lastPaintedCell = null;
    this.hoverCell = null;
    this.renderer.render();
    this.onPostRender?.();
  }

  private handleTouchPainting(touch: Touch): void {
    const coords = this.getCellFromTouchPosition(touch);
    if (!coords) return;

    const cell = this.grid.getCell(coords.row, coords.col);
    if (!cell) return;

    if (cell === this.lastPaintedCell) {
      return;
    }

    if (cell.type === CellType.START || cell.type === CellType.GOAL) {
      return;
    }

    this.grid.setWall(cell.row, cell.col, this.isPainting);
    this.lastPaintedCell = cell;
    this.renderer.render(this.hoverCell, drawModeToPreviewType(this.currentDrawMode, this.isErasing));
    this.onPostRender?.();
  }

  private handleTouchDrawing(touch: Touch): void {
    const coords = this.getCellFromTouchPosition(touch);
    if (!coords) return;

    const cell = this.grid.getCell(coords.row, coords.col);
    if (!cell) return;

    switch (this.currentDrawMode) {
      case DrawMode.START:
        this.placeStart(coords.row, coords.col);
        break;

      case DrawMode.GOAL:
        this.placeGoal(coords.row, coords.col);
        break;
    }
  }

  private getCellFromTouchPosition(touch: Touch): { row: number; col: number } | null {
    let x: number, y: number;

    if (this.viewportController) {
      const canvasPoint = this.viewportController.screenToCanvas(touch.clientX, touch.clientY);
      x = canvasPoint.x;
      y = canvasPoint.y;
    } else {
      const rect = this.canvas.getBoundingClientRect();
      const scaleX = this.canvas.width / rect.width;
      const scaleY = this.canvas.height / rect.height;
      x = (touch.clientX - rect.left) * scaleX;
      y = (touch.clientY - rect.top) * scaleY;
    }

    return this.renderer.cellFromPoint(x, y);
  }

  private handlePainting(event: MouseEvent): void {
    const coords = this.getCellFromMousePosition(event);
    if (!coords) return;

    const cell = this.grid.getCell(coords.row, coords.col);
    if (!cell) return;

    if (cell === this.lastPaintedCell) {
      return;
    }

    if (cell.type === CellType.START || cell.type === CellType.GOAL) {
      return;
    }

    if (this.isErasing) {
      this.grid.setWall(cell.row, cell.col, false);
    } else {
      this.grid.setWall(cell.row, cell.col, this.isPainting);
    }

    this.lastPaintedCell = cell;
    this.renderer.render(this.hoverCell, drawModeToPreviewType(this.currentDrawMode, this.isErasing));
    this.onPostRender?.();
  }

  private handleDrawing(event: MouseEvent): void {
    const coords = this.getCellFromMousePosition(event);
    if (!coords) return;

    const cell = this.grid.getCell(coords.row, coords.col);
    if (!cell) return;

    switch (this.currentDrawMode) {
      case DrawMode.START:
        this.placeStart(coords.row, coords.col);
        break;

      case DrawMode.GOAL:
        this.placeGoal(coords.row, coords.col);
        break;
    }
  }

  private getCellFromMousePosition(event: MouseEvent | KeyboardEvent): { row: number; col: number } | null {
    if (!(event instanceof MouseEvent)) return null;

    let x: number, y: number;

    if (this.viewportController) {
      const canvasPoint = this.viewportController.screenToCanvas(event.clientX, event.clientY);
      x = canvasPoint.x;
      y = canvasPoint.y;
    } else {
      const rect = this.canvas.getBoundingClientRect();
      const scaleX = this.canvas.width / rect.width;
      const scaleY = this.canvas.height / rect.height;
      x = (event.clientX - rect.left) * scaleX;
      y = (event.clientY - rect.top) * scaleY;
    }

    return this.renderer.cellFromPoint(x, y);
  }

  placeStart(row: number, col: number): void {
    this.grid.setStart(row, col);
    this.startPlaced = true;
    this.renderer.render(this.hoverCell, drawModeToPreviewType(this.currentDrawMode, this.isErasing));
    this.onPostRender?.();
  }

  placeGoal(row: number, col: number): void {
    this.grid.setGoal(row, col);
    this.goalPlaced = true;
    this.renderer.render(this.hoverCell, drawModeToPreviewType(this.currentDrawMode, this.isErasing));
    this.onPostRender?.();
  }

  isReadyForPathfinding(): boolean {
    return this.startPlaced && this.goalPlaced;
  }

  reset(): void {
    this.isDrawing = false;
    this.isErasing = false;
    this.isPainting = false;
    this.lastPaintedCell = null;
    this.currentDrawMode = DrawMode.PAINT;
  }

  setDrawMode(mode: DrawMode): void {
    this.currentDrawMode = mode;
    if (this.hoverCell) {
      this.renderer.render(this.hoverCell, drawModeToPreviewType(this.currentDrawMode, this.isErasing));
      this.onPostRender?.();
    }
  }

  setPostRenderCallback(callback: () => void): void {
    this.onPostRender = callback;
  }

  destroy(): void {
    this.canvas.removeEventListener('mousedown', this.boundMouseDown);
    this.canvas.removeEventListener('mousemove', this.boundMouseMove);
    this.canvas.removeEventListener('mouseup', this.boundMouseUp);
    this.canvas.removeEventListener('mouseleave', this.boundMouseLeave);
    this.canvas.removeEventListener('contextmenu', this.boundContextMenu);
    this.canvas.removeEventListener('touchstart', this.boundTouchStart);
    this.canvas.removeEventListener('touchmove', this.boundTouchMove);
    this.canvas.removeEventListener('touchend', this.boundTouchEnd);
  }
}
